import { useMemo } from 'react';
import type { VisibilityMap } from '../../types/VisibilityTypes';
import type { TransientFunnel } from './funnelMapCanvasTypes';
import type { PillsMap } from '../canvas-pills/canvasPillsReducer';
import type { Dictionary } from 'lodash';
import { useCalculateThirdPartyNodes } from './calculate-nodes/useCalculateThirdPartyNodes';
import { useCalculateSfFunnelsNodes } from './calculate-nodes/useCalculateSfFunnelsNodes';
import { useCalculateMetadataElementStandaloneNodes } from './calculate-nodes/useCalculateMetadataElementStandaloneNodes';
import { createFunnelLabelCompositeId } from './helper';
import { useCalculateSectionNodes } from './calculate-nodes/useCalculateSectionNodes';
import type { FunnelStatus } from '../../types/enums/FunnelStatus';
import type { SfFunnelMoreButtonProps } from './types';
import type { CrmOrg } from '../../types/CrmOrgs';
import type { ObjectTypesByOrgId } from '../../hooks/useMultiOrgObjectTypesWithFetch';
import { useCalculateStickyNotesNodes } from './calculate-nodes/useCalculateStickyNotesNodes';
import { useCalculateTextNodes } from './calculate-nodes/useCalculateTextNodes';
import type { CanvasEdge } from '../sweep-canvas/canvas-types';

export const TRANSIENT_GROUP_ID = 'TRANSIENT_GROUP';

export const useFunnelMapElements = ({
  funnelMap,
  transientFunnel,
  renderZeroCountPills,
  visibilityMap,
  orgPillsMap,
  objectTypesByOrgId,
  editingThirdPartyStepId,
  stagesWithSparkleIcon,
  mapForecastCategoryValueToLabel,
  crmOrgs,
  funnelStatusMap,
  sfFunnelMoreButton,
}: {
  funnelMap: FunnelMap;
  visibilityMap: VisibilityMap;
  transientFunnel?: TransientFunnel;
  renderZeroCountPills?: boolean;
  orgPillsMap?: { [crmOrgId: string]: PillsMap };
  objectTypesByOrgId: ObjectTypesByOrgId;
  editingThirdPartyStepId?: string;
  stagesWithSparkleIcon?: string[];
  mapForecastCategoryValueToLabel?: (value: string) => string;
  crmOrgs?: CrmOrg[];
  funnelStatusMap?: Dictionary<FunnelStatus>;
  sfFunnelMoreButton?: SfFunnelMoreButtonProps;
}) => {
  const sfFunnelNodes = useCalculateSfFunnelsNodes({
    funnelMap,
    visibilityMap,
    renderZeroCountPills,
    orgPillsMap,
    transientFunnel,
    objectTypesByOrgId,
    stagesWithSparkleIcon,
    mapForecastCategoryValueToLabel,
    crmOrgs,
    funnelStatusMap,
    sfFunnelMoreButton,
  });
  const thirdPartyNodes = useCalculateThirdPartyNodes({
    funnelMap,
    transientFunnel,
    editingThirdPartyStepId,
    stagesWithSparkleIcon,
  });

  const metadataElementStandaloneNodes = useCalculateMetadataElementStandaloneNodes({
    funnelMap,
    crmOrgs,
    visibilityMap,
  });

  const sectionNodes = useCalculateSectionNodes({
    funnelMap,
  });

  const { stickyNotesNodes } = useCalculateStickyNotesNodes({
    stickyNotesMap: funnelMap.stickyNotes,
    transientFunnel,
  });

  const { textNodes } = useCalculateTextNodes({
    textsMap: funnelMap.texts,
    transientFunnel,
  });

  return useMemo(() => {
    const sweepNodes = [...sfFunnelNodes.nodes, ...thirdPartyNodes.nodes];
    const sweepSections = [...sectionNodes.nodes];
    const sweepGroups = [...sfFunnelNodes.groups, ...thirdPartyNodes.groups];
    const sweepStandaloneNodes = [...metadataElementStandaloneNodes.standaloneNodes];

    // Build a set of all valid node IDs for edge filtering
    const allNodeIdsSet = [...sweepGroups, ...sweepNodes, ...sweepStandaloneNodes].reduce(
      (acc, node) => {
        acc.add(node.id);
        acc.add(createFunnelLabelCompositeId(node.id));
        return acc;
      },
      new Set<string>(),
    );

    // Filter out edges that have a source or target that is not in the sweepNodes, sweepGroups, or sweepStandaloneNodes
    const isEdgeValidFilter = (edge: CanvasEdge) => {
      const sourceNode = allNodeIdsSet.has(edge.source);
      const targetNode = allNodeIdsSet.has(edge.target);
      return sourceNode && targetNode;
    };

    const sweepEdges = [
      ...sfFunnelNodes.edges,
      ...thirdPartyNodes.edges,
      ...metadataElementStandaloneNodes.edges,
    ].filter(isEdgeValidFilter);

    const sweepStickyNotesNodes = [...stickyNotesNodes];

    const sweepTextNodes = [...textNodes];

    return {
      sweepGroups,
      sweepEdges,
      sweepNodes,
      sweepStandaloneNodes,
      sweepSections,
      sweepStickyNotesNodes,
      sweepTextNodes,
    };
  }, [
    sfFunnelNodes.nodes,
    sfFunnelNodes.edges,
    sfFunnelNodes.groups,
    thirdPartyNodes.nodes,
    thirdPartyNodes.edges,
    thirdPartyNodes.groups,
    metadataElementStandaloneNodes.standaloneNodes,
    metadataElementStandaloneNodes.edges,
    sectionNodes.nodes,
    stickyNotesNodes,
    textNodes,
  ]);
};
